import * as React from 'react';
import { compose } from 'ramda';
import { bubbleSortStep, insertionSortStep, selectionSortStep, combSortStep, shellSortStep,
         countingSortStep, cocktailSortStep, gnomeSortStep, brickSortStep } from '../util/algorithm';

import type { StepState } from '../util/algorithm';

function useSort(
    list: number[],
    time: number,
    play: boolean,
    sortStepResolver: (step: StepState) => StepState,
    initialState: StepState,
    highlightResolver: (step: StepState) => number[]
) {
    const [items, setItems] = React.useState(list);
    const [highlight, setHighlight] = React.useState<number[]>([]);
    const [stepState, setStepState] = React.useState<StepState>(initialState);

    React.useEffect(() => {
        setItems(list);
        setStepState(initialState);
        setHighlight([]);
    }, [list])

    React.useEffect(() => {
        if (play) {
            const timeout = setTimeout(() => {
                compose(setStepState, sortStepResolver)(stepState);
                setItems(stepState.list);
                compose(setHighlight, highlightResolver)(stepState)
            }, time);
            return () => { clearTimeout(timeout) }
        }
    }, [stepState, time, sortStepResolver, play, highlightResolver])

    return {
        items,
        highlight
    }
}

function getBubbleInitialState(list: number[]) : StepState {
    return {
        list,
        index: 0,
        keyIndex: list.length,
        flag: false,
        secondFlag: false,
        interval: 1,
        temp: 0,
        gap: 0
    }
}

function useBubbleSort(list: number[], time: number, play: boolean) {
    let initialState = getBubbleInitialState(list);
    const highlight = ({ index } : StepState) => [index];
    return useSort(list, time, play, bubbleSortStep, initialState, highlight);
}

function getInsertionInitialState(list: number[]) : StepState {
    return {
        list,
        index: 0,
        keyIndex: 0,
        flag: false,
        secondFlag: false,
        interval: 1,
        temp: 0,
        gap: 0
    }
}

function useInsertionSort(list: number[], time: number, play: boolean) {
    let initialState = getInsertionInitialState(list);
    const highlight = ({ index }: StepState) => [index];
    return useSort(list, time, play, insertionSortStep, initialState, highlight)
}

function getSelectionInitialState(items: number[]): StepState {
    return {
        list: items,
        index: 0,
        keyIndex: 0,
        flag: false,
        secondFlag: false,
        interval: 1,
        temp: 0,
        gap: 0
    }
}

function useSelectionSort(list: number[], time: number, play: boolean) {
    let initialState = getSelectionInitialState(list);
    const highlight = ({ index, keyIndex }: StepState) => [index, keyIndex];
    return useSort(list, time, play, selectionSortStep, initialState, highlight)
}

function getCombInitialState(items: number[]) : StepState {
    const initialGap = Math.floor( items.length*10/13 );
    return {
        list: items,
        index: initialGap,
        keyIndex: 0,
        flag: false,
        secondFlag: false,
        interval: 0,
        temp: 0,
        gap: initialGap
    }
}

function useCombSort(list: number[], time: number, play: boolean) {
    let initialState = getCombInitialState(list);
    const highlight = ({ index, keyIndex } : StepState) => [index, keyIndex];
    return useSort(list, time, play, combSortStep, initialState, highlight);
}

function getShellInitialState(items: number[]): StepState {
    return {
        list: items,
        index: 0,
        keyIndex: 0,
        flag: false,
        secondFlag: false,
        interval: items.length/2,
        temp: 0,
        gap: 0
    }
}

function useShellSort(list: number[], time: number, play: boolean) {
    let initialState = getShellInitialState(list);
    const highlight = ({ index, keyIndex }: StepState) => [index, keyIndex];
    return useSort(list, time, play, shellSortStep, initialState, highlight)
}

function getCountingInitialState(items: number[]) : StepState {
    return {
        list: items,
        index: 0,
        keyIndex: 0,
        flag: false,
        secondFlag: false,
        interval: 1,
        temp: 0,
        gap: 0
    }
}

function useCountingSort(list: number[], time: number, play: boolean) {
    let initialState = getCountingInitialState(list);
    const highlight = ({ index, keyIndex }: StepState) => [index, keyIndex];
    return useSort(list, time, play, countingSortStep, initialState, highlight)
}

function getCocktailInitialState(items: number[]) : StepState {
    return {
        list: items,
        index: 0,
        keyIndex: items.length - 1,
        flag: false,
        secondFlag: true,
        interval: 1,
        temp: 0,
        gap: 0
    }
}

function useCocktailSort(list: number[], time: number, play: boolean) {
    let initialState = getCocktailInitialState(list);
    const highlight = ({ index }: StepState) => [index];
    return useSort(list, time, play, cocktailSortStep, initialState, highlight)
}

function getGnomeInitialState(items: number[]) : StepState {
    return {
        list: items,
        index: 0,
        keyIndex: items.length,
        flag: false,
        secondFlag: false,
        interval: 1,
        temp: 0,
        gap: 0
    }
}

function useGnomeSort(list: number[], time: number, play: boolean) {
    let initialState = getGnomeInitialState(list);
    const highlight = ({ index }: StepState) => [index];
    return useSort(list, time, play, gnomeSortStep, initialState, highlight)
}

function getBrickInitialState(items: number[]) : StepState {
    return {
        list: items,
        index: 1,
        keyIndex: items.length - 1,
        flag: false,
        secondFlag: true,
        interval: 1,
        temp: 0,
        gap: 0
    }
}

function useBrickSort(list: number[], time: number, play: boolean) {
    let initialState = getBrickInitialState(list);
    const highlight = ({ index }: StepState) => [index];
    return useSort(list, time, play, brickSortStep, initialState, highlight)
}

export {
    useBubbleSort,
    useInsertionSort,
    useSelectionSort,
    useCombSort,
    useShellSort,
    useCountingSort,
    useCocktailSort,
    useGnomeSort,
    useBrickSort
}
